<template>
  <div class="novo-atendimento-page" data-testid="pdv-novo-atendimento-page">
    <div class="page-header">
      <div>
        <h2>{{ t('Novo Atendimento') }}</h2>
        <p>{{ t('Selecione ou crie um cliente para iniciar o atendimento.') }}</p>
      </div>
    </div>

    <div class="cliente-selection">
      <div class="selection-cards">
        <div class="selection-card" data-testid="btn-selecionar-cliente" @click="handleBuscarCliente">
          <i class="pi pi-search"></i>
          <span class="card-title">{{ t('Selecionar Cliente Existente') }}</span>
          <span class="card-desc">{{ t('Buscar em nossa base de clientes cadastrados') }}</span>
        </div>

        <div class="selection-card" data-testid="btn-criar-cliente" @click="handleCriarCliente">
          <i class="pi pi-plus"></i>
          <span class="card-title">{{ t('Criar Novo Cliente') }}</span>
          <span class="card-desc">{{ t('Cadastrar um novo cliente no sistema') }}</span>
        </div>
      </div>

      <div class="selected-client" v-if="selectedClient" data-testid="pdv-cliente-selecionado">
        <div class="client-card">
          <i class="pi pi-user"></i>
          <div class="client-details">
            <span class="client-name" data-testid="pdv-cliente-nome">{{ selectedClient.nome || selectedClient.cliente_nome }}</span>
            <span class="client-phone" v-if="selectedClient.telefone || selectedClient.telefone_completo">{{ selectedClient.telefone_completo || selectedClient.telefone }}</span>
            <span class="client-cpf" v-if="selectedClient.documento">{{ documentLabel }}: {{ selectedClient.documento }}</span>
          </div>
          <Button icon="pi pi-times" severity="secondary" text size="small" data-testid="pdv-cliente-wizard-clear-btn" @click="selectedClient = null" />
        </div>
      </div>

      <div class="action-row" v-if="selectedClient">
        <Button
          :label="t('Continuar para Frente de Caixa')"
          icon="pi pi-arrow-right"
          severity="success"
          size="large"
          :disabled="!selectedClient"
          data-testid="pdv-cliente-wizard-save-btn"
          @click="handleFinish" />
      </div>
    </div>

    <ClienteSelectModal
      ref="clienteSelectRef"
      @select="handleClienteSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ClienteSelectModal from '../../components/ClienteSelectModal.vue';
import { eventBus } from '../../eventBus';
import { useSOCheckout } from '../../composables/useSOCheckout';
import { useCliente } from '../../composables/useCliente';
import { DOCUMENT_PRESENTATION } from '../../composables/useCustomerInternationalConfig';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface ClientRaw {
  cliente_id?: Id;
  id?: Id;
  cliente_nome?: string;
  nome?: string;
  cliente_franquia_id?: Id;
  franquia_id?: Id;
  cliente_ddi?: string;
  ddi?: string;
  cliente_ddd?: string;
  ddd?: string;
  cliente_telefone?: string;
  telefone?: string;
  telefone_completo?: string;
  cliente_cpf?: string;
  documento?: string;
  [key: string]: unknown;
}

interface NormalizedClient {
  cliente_id: number | null;
  cliente_nome: string;
  cliente_franquia_id: number | null;
  cliente_ddi: string;
  cliente_ddd: string;
  cliente_telefone: string;
  cliente_cpf: string;
  [key: string]: unknown;
}

interface ClienteSelectInstance {
  open: () => void;
}

interface CreateOrderResponse {
  dados?: {
    order_id?: Id;
    so_order_id?: Id;
    id?: Id;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const { t } = useI18n();
const router = useRouter();
const { createOrder } = useSOCheckout();
const { fetchCliente, fetchDocumentConfig } = useCliente();

const selectedClient = ref<NormalizedClient | null>(null);
const clienteSelectRef = ref<ClienteSelectInstance | null>(null);

// Document label dinâmico baseado no país da franquia do cliente
const documentType = ref<string>('cpf');
const documentLabel = computed<string>(() =>
  (DOCUMENT_PRESENTATION as Record<string, { label: string }>)[documentType.value]?.label || 'CPF'
);

const normalizeClient = (client: ClientRaw | null | undefined): NormalizedClient | null => {
  if (!client) return null;
  return {
    ...client,
    cliente_id: Number(client.cliente_id || client.id || 0) || null,
    cliente_nome: client.cliente_nome || client.nome || '',
    cliente_franquia_id: Number(client.cliente_franquia_id || client.franquia_id || 0) || null,
    cliente_ddi: client.cliente_ddi || client.ddi || '',
    cliente_ddd: client.cliente_ddd || client.ddd || '',
    cliente_telefone: client.cliente_telefone || client.telefone || '',
    cliente_cpf: client.cliente_cpf || client.documento || '',
  };
};

const loadAndSelectClient = async (client: ClientRaw): Promise<void> => {
  const normalized = normalizeClient(client);
  if (!normalized?.cliente_id) {
    selectedClient.value = normalized;
    return;
  }
  try {
    const fullClient = await fetchCliente(normalized.cliente_id) as ClientRaw;
    // Busca config de documento baseado na franquia do cliente
    const franquiaId = Number(normalized.cliente_franquia_id || (fullClient as any)?.cliente_franquia_id || 0);
    if (franquiaId) {
      try {
        // `fetchDocumentConfig` vem do useCliente() instanciado no topo do
        // setup, e NÃO de um useCliente() aqui dentro — chamado 2026-0028.
        // Este bloco roda depois de `await fetchCliente(...)`, quando o Vue já
        // perdeu o componente ativo, e useCliente() chama useI18n(), que recusa
        // fora de setup(). O catch logo abaixo engolia essa recusa como se
        // fosse "config indisponível": o tipo de documento da franquia nunca
        // era aplicado e a tela ficava sempre em cpf.
        const config = await fetchDocumentConfig(franquiaId) as { default_document_type?: string } | null;
        if (config?.default_document_type) {
          documentType.value = config.default_document_type;
        }
      } catch (e) {
        // Non-fatal: keep the default document type (cpf), but never silently.
        logError('PDVNovoAtendimentoPage', 'loadAndSelectClient', 'Document config unavailable, keeping cpf', e);
      }
    }

    selectedClient.value = normalizeClient(fullClient);
  } catch (err) {
    logError('PDVNovoAtendimentoPage', 'loadAndSelectClient', 'Error loading full client', err);
    selectedClient.value = normalized;
  }
};

const handleFinish = async (): Promise<void> => {
  if (!selectedClient.value) return;

  const clienteId = Number(selectedClient.value.cliente_id || 0);
  const franchiseId = Number(selectedClient.value.cliente_franquia_id || (selectedClient.value as { franquia_id?: Id }).franquia_id || 0);

  if (!clienteId || !franchiseId) {
    eventBus.emit('globalMessage.error', t('Cliente sem franquia válida para iniciar atendimento'));
    return;
  }

  log('PDVNovoAtendimentoPage', 'handleFinish', 'Creating order for client', clienteId);
  try {
    const result = await createOrder({
      so_order_client_id: clienteId,
      so_order_franchise_id: franchiseId,
      so_order_origin: 'balcao'
    }) as unknown as CreateOrderResponse;

    const orderId = result.dados?.order_id || result.dados?.so_order_id || result.dados?.id;
    eventBus.emit('globalMessage.success', t('Atendimento iniciado'));
    router.push(`/pdv/checkout/${orderId}`);
  } catch (err) {
    // The error toast is emitted by useSOCheckout.createOrder itself.
    logError('PDVNovoAtendimentoPage', 'handleFinish', 'Error', err);
  }
};

const handleBuscarCliente = (): void => {
  log('PDVNovoAtendimentoPage', 'handleBuscarCliente', 'Opening client select DataTable modal');
  if (clienteSelectRef.value) {
    clienteSelectRef.value.open();
  }
};

const handleCriarCliente = (): void => {
  log('PDVNovoAtendimentoPage', 'handleCriarCliente', 'Opening client create modal');
  eventBus.emit('modal.cliente.open', { action: 'create' });
};

const handleClienteSelected = (client: ClientRaw): void => {
  log('PDVNovoAtendimentoPage', 'handleClienteSelected', 'Client selected from table', client);
  loadAndSelectClient(client);
};

const handleClienteSaved = (data?: ClientRaw): void => {
  log('PDVNovoAtendimentoPage', 'handleClienteSaved', 'New client created, auto-selecting', data);
  if (data?.cliente_id) {
    loadAndSelectClient(data);
  }
};

onMounted(() => {
  eventBus.on('cliente.saved', handleClienteSaved);
});

onBeforeUnmount(() => {
  eventBus.off('cliente.saved', handleClienteSaved);
});
</script>

<style scoped>
.novo-atendimento-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0 0 8px 0;
}

.page-header p {
  margin: 0;
  color: var(--p-text-muted-color);
}

.cliente-selection {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.selection-cards {
  display: flex;
  gap: 16px;
}

.selection-card {
  flex: 1;
  border: 2px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.selection-card:hover {
  border-color: var(--p-primary-color);
  background: var(--p-primary-50);
}

.selection-card i {
  font-size: 2rem;
  color: var(--p-primary-color);
  margin-bottom: 12px;
  display: block;
}

.card-title {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

.card-desc {
  display: block;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.client-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-200);
  border-radius: 8px;
  padding: 16px;
}

.client-card > i {
  font-size: 1.5rem;
  color: var(--p-primary-color);
}

.client-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.client-name { font-weight: 600; }

.client-phone,
.client-cpf { font-size: 0.9rem; color: var(--p-text-muted-color); }

.action-row {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
</style>
